import { Viewer, Math as CesiumMath, Cartesian3, Rectangle, EasingFunction, BoundingSphere, Cartographic } from "cesium";
import { ToolTipArrowType } from "../interfaces/utils";
import { TileModel } from "src/class/tileModel";

const colorIsolateDefault = [204, 203, 209, 0.02];
//#region Varaiable
export const colorIsolate = `rgba(${colorIsolateDefault[0]},${colorIsolateDefault[1]},${colorIsolateDefault[2]},${colorIsolateDefault[3]})`
export const colorDefaultByTask = 'rgba(240,240,240,1)'
export const colorElementDefault = 'rgba(255,255,255,1)'
//Tạo UUID ngẫu nhiên
export const createUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

// Hàm chuyển đổi RGBA sang HEX
export const rgbaToHex = (r: number, g: number, b: number, a: number): string => {
    const rHex = Math.round(r).toString(16).padStart(2, '0');
    const gHex = Math.round(g).toString(16).padStart(2, '0');
    const bHex = Math.round(b).toString(16).padStart(2, '0');
    const aHex = a < 1 ? Math.round(a * 255).toString(16).padStart(2, '0') : '';

    return `#${rHex}${gHex}${bHex}${aHex}`;
}

//Hàm chuyển đổi hex sang RGBA
export const hexToCesiumColor = (hex: string, opacity: number = 1) => {
    const bigint = parseInt(hex.replace("#", ""), 16);
    const r = ((bigint >> 16) & 255) / 255; // Giá trị đỏ (từ 0-1)
    const g = ((bigint >> 8) & 255) / 255;  // Giá trị xanh lá
    const b = (bigint & 255) / 255;         // Giá trị xanh dương
    return `color(${r}, ${g}, ${b}, ${opacity})`; // Trả về định dạng Cesium
}

export const rgbaToCesiumFormat = (r: number, g: number, b: number, a: number) => {
    /**
    * Chuyển đổi màu RGBA từ hệ 0-255 về hệ Cesium (0-1),
    * trong đó alpha sẽ được làm tròn về hệ 8-bit (0-255).
    */
    const red = r / 255;
    const green = g / 255;
    const blue = b / 255;
    const alpha = Math.round(a * 255) / 255; // Làm tròn alpha về hệ 8-bit

    return {
        red,
        green,
        blue,
        alpha
    };
}

export const colorIsolateCesium = rgbaToCesiumFormat(colorIsolateDefault[0], colorIsolateDefault[1], colorIsolateDefault[2], colorIsolateDefault[3])

/**
    * Chặn các ký tự không hợp lệ và chỉ cho phép nhập số nguyên
 */
export const restrictToIntegerInput = (event: KeyboardEvent) => {
    // Cho phép các phím điều khiển: backspace, tab, arrow keys
    if (
        event.key === "Backspace" ||
        event.key === "Tab" ||
        event.key === "ArrowLeft" ||
        event.key === "ArrowRight"
    ) {
        return;
    }

    // Chặn ký tự không phải số
    if (!/^\d$/.test(event.key)) {
        event.preventDefault();
    }
};

/**
 * Chuẩn hóa mã màu HEX.
    */

export const normalizeHexColor = (hexColor: string): string => {
    // Kiểm tra định dạng mã màu hợp lệ
    if (!/^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(hexColor)) {
        throw new Error("Invalid hex color format");
    }

    // Nếu là dạng #RRGGBBAA (8 chữ số)
    if (hexColor.length === 9) {
        return hexColor.substring(0, 7); // Lấy 6 ký tự đầu (#RRGGBB)
    }

    // Nếu là dạng #RRGGBB (6 chữ số), trả lại chính nó
    return hexColor;
};
/**
 * Chuẩn hóa mã màu HEX với Opacity.
    */
export const convertHexWithOpacity = (hex: string, opacity: number): string => {
    // Xử lý trường hợp HEX không hợp lệ
    if (!/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/.test(hex)) {
        throw new Error("Invalid HEX color format");
    }

    // Đảm bảo opacity nằm trong khoảng [0, 1]
    if (opacity < 0 || opacity > 1) {
        throw new Error("Opacity must be a value between 0 and 1");
    }

    // Lấy phần R, G, B từ HEX
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // Tính giá trị alpha (opacity) dưới dạng HEX (00 đến FF)
    const alpha = Math.round(opacity * 255).toString(16).padStart(2, "0");

    // Trả về HEX bao gồm alpha
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}${alpha}`;
}

/**
* Lấy mã màu từ localStorage hoặc trả về giá trị mặc định.

*/
export const getColorFromLocalStorage = (defaultColor: string = "#14CC6D"): string => {
    const savedColor = localStorage.getItem("selectedColor");

    if (!savedColor) {
        return defaultColor
    }

    // Trả về màu mặc định nếu không tìm thấy hoặc không hợp lệ
    return savedColor;
};


export const watchElement = (
    element: HTMLElement | null,
    callback: (info: { element: HTMLElement; width: number; height: number }) => void
): void => {
    if (!element) {
        console.warn("Element không hợp lệ!");
        return;
    }

    // Tạo ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
            const { width, height } = entry.contentRect;
            callback({ element, width, height });
        });
    });

    // Bắt đầu theo dõi
    resizeObserver.observe(element);
};


export const watchElementBySelector = (
    selector: string,
    callback: (info: { element: HTMLElement; width: number; height: number }) => void
): void => {
    const element = document.querySelector(selector) as HTMLElement | null;
    if (!element) {
        console.warn(`Không tìm thấy thẻ với selector: ${selector}`);
        return;
    }

    // Tạo ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
            const { width, height } = entry.contentRect;
            callback({ element, width, height });
        });
    });

    // Bắt đầu theo dõi
    resizeObserver.observe(element);
};



export const handleWindowResizeByElement = (
    element: HTMLElement,
    callback?: (info: { element: HTMLElement; width: number; height: number }) => void
): void => {
    const { width, height } = element.getBoundingClientRect();

    // Gọi callback nếu có
    if (callback) {
        callback({ element, width, height });
    }


};

// Hàm mới: Nhận id thay vì element
export const handleWindowResizeBySelector = (
    elementId: string,
    callback?: (info: { element: HTMLElement; width: number; height: number }) => void
): void => {
    const element = document.querySelector(elementId) as HTMLElement;

    if (!element) {
        console.warn(`Không tìm thấy phần tử với Id: ${elementId}`);
        return;
    }

    // Gọi hàm handleWindowResize với element tìm được
    handleWindowResizeByElement(element, callback);
};

//#region flyToVietNam
export const flyToVietNam = (viewer: Viewer) => {

    // Các điểm mà bạn cung cấp dưới dạng Cartesian (x, y, z)
    const points = [
        new Cartesian3(-1229722.8124160052, 5713984.470134131, 2544118.932881014),
        new Cartesian3(-2298759.665631677, 5366849.124046434, 2558782.184002856),
        new Cartesian3(-2461357.400472914, 5827707.957234764, 807980.8223163928),
        new Cartesian3(-1362893.9246498803, 6167747.453515315, 879552.6464279576)
    ];

    // Chuyển đổi các điểm từ Cartesian sang Longitude, Latitude
    const ellipsoid = viewer.scene.globe.ellipsoid;
    const latitudes: any = [];
    const longitudes: any = [];

    points.forEach(point => {
        const cartographic = ellipsoid.cartesianToCartographic(point);
        latitudes.push(CesiumMath.toDegrees(cartographic.latitude));
        longitudes.push(CesiumMath.toDegrees(cartographic.longitude));
    });

    // Tính toán các giá trị cực trị (tối thiểu và tối đa) của longitude và latitude
    const minLat = Math.min(...latitudes);
    const maxLat = Math.max(...latitudes);
    const minLon = Math.min(...longitudes);
    const maxLon = Math.max(...longitudes);

    // Tạo hình chữ nhật từ các giá trị cực trị
    const rectangle = Rectangle.fromDegrees(minLon, minLat, maxLon, maxLat);

    // Di chuyển camera đến vùng hình chữ nhật vừa tạo
    viewer.camera.flyTo({
        destination: rectangle,
        duration: 0
    });


}

//#region save camera
export const saveCurrentView = (viewer: Viewer) => {
    const camera = viewer.scene.camera;

    // Lưu thông tin camera (position, direction, up)
    const cameraView = {
        position: camera.position,
        direction: camera.direction,
        up: camera.up,
    };

    // Trả về chuỗi JSON chứa thông tin camera
    return JSON.stringify(cameraView);
}

//#region  restore Camera
export const restoreSavedView = (viewer: Viewer, obj: string, durationTime = 0.5) => {
    // Parse chuỗi JSON thành đối tượng
    const tempSaveCamera = JSON.parse(obj);
    if (!tempSaveCamera) {
        console.error('No saved camera view to restore');
        return;
    }

    // Di chuyển camera đến vị trí và hướng đã lưu
    viewer.camera.flyTo({
        destination: new Cartesian3(tempSaveCamera.position.x, tempSaveCamera.position.y, tempSaveCamera.position.z),
        orientation: {
            direction: new Cartesian3(tempSaveCamera.direction.x, tempSaveCamera.direction.y, tempSaveCamera.direction.z),
            up: new Cartesian3(tempSaveCamera.up.x, tempSaveCamera.up.y, tempSaveCamera.up.z)
        },
        easingFunction: EasingFunction.QUADRATIC_IN_OUT,
        duration: durationTime
    });
}
//#region base64ToFile
export const base64ToFile = (base64: string, filename: string): File => {
    // Xử lý chuỗi Base64, loại bỏ phần tiền tố "data:@file/octet-stream;base64,"
    const base64Data = base64.split(',')[1];

    // Giải mã chuỗi Base64
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset++) {
        const byte = byteCharacters.charCodeAt(offset);
        byteArrays.push(byte);
    }

    const byteArray = new Uint8Array(byteArrays);

    // Tạo một đối tượng File từ mảng byte
    const file = new File([byteArray], filename, { type: 'application/octet-stream' });

    return file;
}
//#region createBoundingSphereFromCartesianBounds

export const createBoundingSphereFromCartesianBounds = (min: Cartesian3, max: Cartesian3) => {
    const center = Cartesian3.add(min, max, new Cartesian3());
    Cartesian3.multiplyByScalar(center, 0.5, center); // Trung điểm
    const radius = Cartesian3.distance(min, max) / 2;
    return new BoundingSphere(center, radius);
}
//#region createBoundingSphereFromLatLonBounds

export const createBoundingSphereFromLatLonBounds = (
    minCoord: [number, number, number],
    maxCoord: [number, number, number]
) => {
    const min = Cartesian3.fromDegrees(minCoord[1], minCoord[0], minCoord[2]);
    const max = Cartesian3.fromDegrees(maxCoord[1], maxCoord[0], maxCoord[2]);
    return createBoundingSphereFromCartesianBounds(min, max);
};
//#region flyToBoundingBox

export const flyToBoundingBox = (viewer: Viewer, boundingBox: BoundingSphere, duration: number = 0) => {
    viewer.camera.flyToBoundingSphere(boundingBox, {
        duration: duration,
    });
}



//#region showToolTipByElement
export const showToolTipByElement = (
    element: {
        parentElement: HTMLElement,
        childElement: HTMLElement,
        attribute?: string,
        toolTip?: string,
        typeArrow?: ToolTipArrowType
    }) => {

    const toolTip = createElementTooltip({
        toolTip: element.toolTip,
        typeArrow: element.typeArrow || ToolTipArrowType.Top
    });
    // Thêm tooltip vào DOM tạm thời để lấy kích thước
    let tooltipTimeout: ReturnType<typeof setTimeout> | undefined; // Biến lưu trữ timeout ID

    element.childElement.addEventListener("mouseover", (event) => {

        element.parentElement.appendChild(toolTip);


        tooltipTimeout = setTimeout(() => {

            // Tính toán vị trí tooltip
            calculateTooltipPosition(element.parentElement, element.childElement, toolTip, element.typeArrow || ToolTipArrowType.Top);
            // Hiển thị tooltip sau khi tính toán xong
            toolTip.style.visibility = "visible"; // Đảm bảo tooltip hiển thị sau khi tính toán vị trí
        }, 2000)


    });

    element.childElement.addEventListener("mouseout", (event) => {
        // Hủy bỏ thời gian trì hoãn nếu chuột rời đi trước khi tooltip hiển thị
        if (tooltipTimeout) {
            clearTimeout(tooltipTimeout);
        }

        // Ẩn tooltip và xóa tooltip khỏi DOM khi không hover
        toolTip.style.visibility = "hidden"; // Đảm bảo tooltip hiển thị sau khi tính toán vị trí
        toolTip.remove();  // Xóa tooltip khỏi DOM
    });
}

//#region calculateTooltipPosition
const calculateTooltipPosition = (parent: HTMLElement, child: HTMLElement, toolTip: HTMLElement, arrowType: ToolTipArrowType) => {
    // Lấy vị trí của thẻ cha
    const rectParent = parent?.getBoundingClientRect();
    // Lấy vị trí của thẻ con
    const rectChild = child.getBoundingClientRect();
    // Tính toán vị trí và kích thước của tooltip sau khi nó đã được thêm vào DOM
    const widthToolTip = toolTip.getBoundingClientRect().width;
    const heightToolTip = toolTip.getBoundingClientRect().height;

    // Tính toán vị trí của thẻ con so với thẻ cha
    switch (arrowType) {
        case ToolTipArrowType.Top:
            toolTip.style.top = `${rectChild.top - rectParent!.top - rectChild.height - 10}px`;
            toolTip.style.left = `${rectChild.left - rectParent!.left - widthToolTip / 2 + rectChild.width / 2}px`;
            break;
        case ToolTipArrowType.TopLeft:
            toolTip.style.top = `${rectChild.top - rectParent!.top - rectChild.height - 10}px`;
            toolTip.style.left = `${rectChild.left - rectParent!.left + rectChild.width / 2 - 7}px`;
            break;
        case ToolTipArrowType.TopRight:
            toolTip.style.top = `${rectChild.top - rectParent!.top - rectChild.height - 10}px`;
            toolTip.style.left = `${rectChild.left - rectParent!.left + rectChild.width / 2 - widthToolTip + 7}px`;
            break;
        case ToolTipArrowType.Bottom:
            toolTip.style.top = `${rectChild.bottom - rectParent!.top + 10}px`;
            toolTip.style.left = `${rectChild.left - rectParent!.left - widthToolTip / 2 + rectChild.width / 2}px`;
            break

        case ToolTipArrowType.BottomLeft:
            toolTip.style.top = `${rectChild.bottom - rectParent!.top + 10}px`;
            toolTip.style.left = `${rectChild.left - rectParent!.left + rectChild.width / 2 - 7}px`;
            break;

        case ToolTipArrowType.BottomRight:
            toolTip.style.top = `${rectChild.bottom - rectParent!.top + 10}px`;
            toolTip.style.left = `${rectChild.left - rectParent!.left + rectChild.width / 2 - widthToolTip + 7}px`;
            break;

        case ToolTipArrowType.Left:
            toolTip.style.top = `${rectChild.top - rectParent!.top - heightToolTip / 2 + rectChild.height / 2}px`;
            toolTip.style.left = `${rectChild.left - rectParent!.left - widthToolTip - 10}px`;
            break;
        case ToolTipArrowType.Right:
            toolTip.style.top = `${rectChild.top - rectParent!.top - heightToolTip / 2 + rectChild.height / 2}px`;
            toolTip.style.left = `${rectChild.right - rectParent!.left + 10}px`;
            break;

        default:
            break;
    }
}

//#region createElementTooltip
const createElementTooltip = (data: {
    toolTip?: string,
    typeArrow: ToolTipArrowType | ToolTipArrowType.Top
}) => {
    const toolTip = document.createElement("div");
    toolTip.className = `dynamic-tooltip ${data.typeArrow}`;
    toolTip.style.zIndex = "9999";
    toolTip.innerText = data.toolTip || "Tooltip content here"; // Nội dung tooltip mặc định
    toolTip.style.display = "block"; // Tooltip ẩn khi tạo ra
    toolTip.style.visibility = "hidden"; // Đảm bảo tooltip không hiển thị ngay lập tức
    return toolTip;
}

//#region compareModelMatrix
export const compareModelMatrix = (matrix1: { [key: number]: number }, matrix2: { [key: number]: number }) => {
    // Lặp qua tất cả các keys trong matrix1
    for (let key in matrix1) {
        // Kiểm tra xem key có tồn tại trong matrix2 và so sánh giá trị của chúng
        if (matrix1[key] !== matrix2[key]) {
            return false; // Nếu có sự khác biệt, trả về false
        }
    }
    return true; // Nếu tất cả các giá trị giống nhau, trả về true
}

//#region handleTreeModelTile
// Hàm xử lý lại cây model trước khi cho trả về cây chính

export const handleTreeModelTile = (tree: TileModel[]) => {
    try {
        tree.forEach((item) => {
            if (item.isFolder) {
                const children = findChildren(tree, item.id); // Tìm các con của item
                const boundingBox = item.getCombinedBoundingBox(children);
                if (boundingBox) {
                    const { min, max } = boundingBox;
                    // Use min and max here
                    // Tạo BoundingSphere từ min và max
                    const center = new Cartesian3(
                        (min.x + max.x) / 2,
                        (min.y + max.y) / 2,
                        (min.z + max.z) / 2
                    );
                    item.originalCartgraphic = Cartographic.fromCartesian(center);
                }

                if (hasAnyValidInput(item.inputObjs)) {
                    // Tính toán delta (độ thay đổi) từ vị trí cũ sang vị trí mới
                    const deltaLongitude = item.inputObjs!.longitude! - CesiumMath.toDegrees(item.originalCartgraphic?.longitude);
                    const deltaLatitude = item.inputObjs!.latitude! - CesiumMath.toDegrees(item.originalCartgraphic?.latitude);


                    children.forEach((child: TileModel) => {
                        const itemFromTree = tree.find(x => x.id === child.id);
                        if (itemFromTree && itemFromTree.originalCartgraphic) {
                            // Tạo bản sao mới cho inputObjs để tránh sửa đổi trực tiếp đối tượng gốc
                            const newInputObjs = { ...itemFromTree.inputObjs! }; // Sao chép inputObjs
                            const newLongitude = CesiumMath.toDegrees(itemFromTree.originalCartgraphic?.longitude) + deltaLongitude;
                            const newLatitude = CesiumMath.toDegrees(itemFromTree.originalCartgraphic?.latitude) + deltaLatitude;

                            // Cập nhật giá trị mới vào bản sao của inputObjs
                            newInputObjs.longitude = newLongitude;
                            newInputObjs.latitude = newLatitude;

                            // Gán lại giá trị mới cho inputObjs
                            itemFromTree.inputObjs = newInputObjs;
                        }
                    });
                }
            }
        });

        tree.forEach((item) => {
            if (!item.isFolder) {
                const findParent = tree.find((x: any) => x.id === item.parentID)
                if (findParent && !findParent.isShow) {
                    item.isShow = false
                } else {
                    item.sceneManager.setPositionModel(item.tileset, item.inputObjs)
                    if (!item.isShow) {

                        item.isShow = false
                    }
                }


            }
        })

    } catch (error) {

        console.log(error);
    }

}


// Hàm đệ quy để tìm con của đối tượng có parentID
// export function findChildren(data: any[], parentId: string): any[] {
//   let children: any[] = [];

//   for (let item of data) {
//     if (item.parentID === parentId) {
//       children.push(item);
//       // Đệ quy lấy con của item, rồi push từng phần tử vào mảng children
//       const subChildren = findChildren(data, item.id);
//       children.push(...subChildren);
//     }
//   }

//   return children;
// }
export function findChildren(data: any[], parentId: string): any[] {
    let children: any[] = [];
    let stack: string[] = [parentId];

    while (stack.length) {
        const currentId = stack.pop();
        for (let item of data) {
            if (item.parentID === currentId) {
                children.push(item);
                stack.push(item.id);
            }
        }
    }

    return children;
}
//#region hasAnyValidInput
export const hasAnyValidInput = (obj: any): boolean => {
    if (!obj) return false;
    const keys = ['x', 'y', 'z', 'height', 'latitude', 'longitude'];
    return keys.some(key => obj[key] !== undefined && obj[key] !== null);
}

//#region findBoundingBox
export const findBoundingBoxFromTileModel = (children: TileModel[]) => {
    let boundingVolumes: BoundingSphere[] = [];
    if (children.length == 0) return;
    children.forEach((item: TileModel) => {
        if (item.tileset) {

            if (item.contentType === '3dtile') {
                const boundingVolume = item.tileset.boundingSphere;
                if (boundingVolume) {
                    boundingVolumes.push(boundingVolume);
                }
            } else if (item.contentType === 'kmz') {
                const boundKmz = item.sceneManager.getBoundingBoxFromKMZ(item.tileset);
                if (boundKmz) {
                    boundingVolumes.push(boundKmz)
                }
            }
        }
    })
    if (boundingVolumes.length === 0) return;
    // Khởi tạo điểm min và max ban đầu
    let combinedMin: Cartesian3 = new Cartesian3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
    let combinedMax: Cartesian3 = new Cartesian3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);

    // Lặp qua tất cả các bounding volumes để tính toán min/max
    boundingVolumes.forEach((boundingVolume) => {
        const center = boundingVolume.center;
        const radius = boundingVolume.radius;

        // Tính toán các điểm cực trị (min, max) cho từng BoundingSphere
        const volumeMin = new Cartesian3(center.x - radius, center.y - radius, center.z - radius);
        const volumeMax = new Cartesian3(center.x + radius, center.y + radius, center.z + radius);

        // Cập nhật điểm min và max chung
        combinedMin = new Cartesian3(
            Math.min(combinedMin.x, volumeMin.x),
            Math.min(combinedMin.y, volumeMin.y),
            Math.min(combinedMin.z, volumeMin.z)
        );

        combinedMax = new Cartesian3(
            Math.max(combinedMax.x, volumeMax.x),
            Math.max(combinedMax.y, volumeMax.y),
            Math.max(combinedMax.z, volumeMax.z)
        );
    });



    return { min: combinedMin, max: combinedMax };
}

// ext-color.ts
export type ExtColorMap = Record<string, string>;

export const BASE_COLORS: ExtColorMap = {
    // yêu cầu chính
    folder: "#F4B400", // vàng folder
    ifc: "#1E88E5", // BIM/IFC blue
    kmz: "#5800ccff", // Earth/Geo blue (distinct from other greens)
    mpp: "#1b9545", // MS Project orange (distinct)
    fbx: "#E91E63", // 3D/pipeline pink

    // bonus hay gặp (có thể bỏ nếu muốn tối giản)
    pdf: "#D32F2F",
    doc: "#2A56C6", docx: "#2A56C6",
    xls: "#27AE60", xlsx: "#27AE60",
    text: "#5D4037", // fallback
    ppt: "#D24726", pptx: "#D24726",
    jpg: "#8E24AA", jpeg: "#8E24AA", png: "#8E24AA", gif: "#8E24AA", webp: "#8E24AA", svg: "#8E24AA",
    mp4: "#5E35B1", mov: "#5E35B1", mkv: "#5E35B1",
    zip: "#5D4037", rar: "#5D4037", "7z": "#5D4037",
    obj: "#FF7043", glb: "#FF7043", gLtf: "#FF7043", gltf: "#FF7043",
    // Cesium / 3D Tiles related
    "3dtiles": "#009688", "3dtile": "#009688", "3dtileset": "#00695C", "tileset": "#00695C", "tileset.json": "#006064",
    b3dm: "#00838F", i3dm: "#00796B", cmpt: "#004D40", pnts: "#00ACC1",
    // Navisworks / Office aliases (distinct colors)
    nwd: "#FF6D00", nwc: "#9C27B0", excel: "#27AE60",
    dwg: "#33be89ff", dxf: "#cf6a89ff",
    rvt: "#0D47A1",
};

export function showStatusStack(containerId: string, content: string): { remove: () => void } {
    let parent = document.getElementById(containerId) || document.body;
    let container = parent.querySelector("#status-stack-container");
    if (!container) {
        container = document.createElement("div");
        container.id = "status-stack-container";
        parent.appendChild(container);
    }

    const item = document.createElement("div");
    item.className = "status-stack-item";

    const textSpan = document.createElement("span");
    textSpan.className = "status-stack-text";
    textSpan.innerText = content;
    item.appendChild(textSpan);

    container.appendChild(item);

    return {
        remove: () => {
            item.remove();
            if (container && container.childElementCount === 0) {
                container.remove();
            }
        }
    };
}
